import React from "react";

import { useTranslation } from "utils";

import { OnboardingPracticeKey } from "consts";

import { Icon, SoilSightLoader, Switch, Tooltip, Typography } from "components";

import { formatUpliftPercent } from "pages/app/scenario-analysis/utils/uplift";
import { useApproxValue } from "pages/app/scenario-onboarding/hooks/useApproxValue";

export type PracticeToggleItem = {
  key: OnboardingPracticeKey;
  enabled: boolean;
  /** The rotation already matches this practice either way, so the switch has nothing to apply. */
  isInert?: boolean;
};

type PracticeToggleCardProps = {
  items: PracticeToggleItem[];
  totalUpliftPct: number | null;
  isCalculating: boolean;
  onToggle: (key: OnboardingPracticeKey, enabled: boolean) => void;
};

// Step-3 "biggest levers" card: per-practice toggles and the live total uplift, dimmed while recalculating.
export const PracticeToggleCard = ({ items, totalUpliftPct, isCalculating, onToggle }: PracticeToggleCardProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const approx = useApproxValue();

  return (
    <div className={`onboarding-practice-card${isCalculating ? " onboarding-practice-card--calculating" : ""}`} data-testid="onboarding-practice-toggles">
      {isCalculating && (
        <div className="onboarding-practice-card__overlay" data-testid="onboarding-practice-updating">
          <SoilSightLoader size="m" className="onboarding-practice-card__loader" dataTestId="onboarding-practice-updating-spinner" />
        </div>
      )}
      <div className="onboarding-practice-card__body">
        <div className="onboarding-practice-card__header">
          <span className="onboarding-practice-card__leaf">
            <Icon iconName="klim/plant-ai" size="m-light" />
          </span>
          <Typography variant="paragraph-m" className="onboarding-practice-card__title">
            {t("onboarding.step3.practiceCard.title")}
          </Typography>
          <div className="onboarding-practice-card__total">
            <Typography variant="h1" data-testid="onboarding-total-uplift">
              {totalUpliftPct == null ? "—" : approx(formatUpliftPercent(totalUpliftPct))}
            </Typography>
            <Tooltip title={t("onboarding.step3.practiceCard.totalTooltip")} placement="top">
              <span className="onboarding-practice-card__info">
                <Icon iconName="tabler/info-circle" size="s" />
              </span>
            </Tooltip>
          </div>
        </div>
        <div className="onboarding-practice-card__items">
          {items.map((item) => (
            <div key={item.key} className="onboarding-practice-card__item">
              <span className="onboarding-practice-card__item-label">
                <Typography variant="paragraph-m" noWrap>
                  {t(`onboarding.step3.practices.${item.key}.label`)}
                </Typography>
                <Tooltip title={t(`onboarding.step3.practices.${item.key}.tooltip`)} placement="top">
                  <span className="onboarding-practice-card__info">
                    <Icon iconName="tabler/info-circle" size="s" />
                  </span>
                </Tooltip>
              </span>
              <Switch
                checked={item.enabled}
                disabled={isCalculating || Boolean(item.isInert)}
                onChange={(checked: boolean) => onToggle(item.key, checked)}
                data-testid={`onboarding-toggle-${item.key}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
